/// <amd-module name="Build/Admin/Pool/Components/MaintenanceJobsList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";

import { Ago } from "VSSUI/Ago";
import { Duration } from "VSSUI/Duration";
import { Link, ILinkProps } from "VSSUI/Link";
import { Status, Statuses, IStatusProps, StatusSize } from "VSSUI/Status";
import { Table, ITableColumn, SimpleTableCell, TableColumnStyle, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TaskAgentPoolMaintenanceJob, TaskAgentPoolMaintenanceJobStatus, TaskAgentPoolMaintenanceJobResult } from "DistributedTask/Client/Contracts/TaskAgent";

import { SignalRState } from "Build/Admin/Common/Stores/SignalRStore";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IMaintenanceJobsListProps {
    jobs: TaskAgentPoolMaintenanceJob[];
    signalRState: SignalRState;
}

class TableListItem {
    value: TaskAgentPoolMaintenanceJob;
    linkProps: ILinkProps;
}

export class MaintenanceJobsList extends VssComponent<IMaintenanceJobsListProps> {
    public render(): JSX.Element {
        return (
            <Table<TableListItem>
                className="pipelines-maintenance-jobs-list"
                columns={this._columns}
                itemProvider={new ArrayItemProvider<TableListItem>(this._buildListItems())}
            />
        );
    }

    private _buildListItems(): TableListItem[] {
        if (this.props.jobs) {
            return this.props.jobs.map(job => { return { value: job } as TableListItem });
        }
        
        return [];
    }

    private _renderIdCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                line1={this._renderPrimaryNameRowText(tableItem.value)}
                line2={""}
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderJobStatusIcon(tableItem.value, className)
                }}
            />
        );
    }

    private _renderJobStatusIcon(job: TaskAgentPoolMaintenanceJob, className?: string | undefined): JSX.Element {
        return <Status {...this._getStatusPropsForJob(job)} className={className} size={StatusSize.l} />;
    }

    private _renderPrimaryNameRowText(job: TaskAgentPoolMaintenanceJob): JSX.Element {
        return (
            <Tooltip overflowOnly={true}>
                <span className="fontWeightSemiBold text-ellipsis bolt-list-cell-text link-left-padding">
                    {format(Resources.TaskAgentJobNumberFormat, job.jobId)}
                </span>
            </Tooltip>
        );
    }

    private _getStatusPropsForJob(job: TaskAgentPoolMaintenanceJob): IStatusProps {
        if (job.status !== TaskAgentPoolMaintenanceJobStatus.Completed) {
            switch (job.status) {
                case TaskAgentPoolMaintenanceJobStatus.Cancelling: 
                case TaskAgentPoolMaintenanceJobStatus.InProgress:
                    return Statuses.Running;   
                case TaskAgentPoolMaintenanceJobStatus.Queued:
                default:
                    return Statuses.Queued;
            }
        }
        else {
            switch (job.result) {
                case TaskAgentPoolMaintenanceJobResult.Canceled:
                    return Statuses.Canceled;
                case TaskAgentPoolMaintenanceJobResult.Failed:
                    return Statuses.Failed;
                case TaskAgentPoolMaintenanceJobResult.Succeeded:
                default:
                    return Statuses.Success;
            }
        }
    }

    private _renderErrorsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.status === TaskAgentPoolMaintenanceJobStatus.Completed &&
                    format(Resources.ErrorsAndWarningsCellText, tableItem.value.errorCount, tableItem.value.warningCount)
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderQueuedTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.queueTime &&
                    <Ago date={tableItem.value.queueTime} format={AgoFormat.Extended} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderStartTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.startTime &&
                    <Ago date={tableItem.value.startTime} format={AgoFormat.Extended} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderDurationCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.finishTime &&
                    <Duration startDate={tableItem.value.startTime} endDate={this.props.signalRState === SignalRState.Disconnected && !tableItem.value.finishTime ? new Date(Date.now()) : tableItem.value.finishTime} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderTargetAgentsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.value.status === TaskAgentPoolMaintenanceJobStatus.Completed &&
                    format(Resources.TargetAgentsCellText, (tableItem.value.targetAgents && tableItem.value.targetAgents.length) || 0)
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderLogsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    // Jobs that had no agents to run on will still have log download URLs, but they'll 404, so we need to do this check
                    tableItem.value.targetAgents &&
                    tableItem.value.targetAgents.length > 0 &&
                    tableItem.value.logsDownloadUrl &&
                    <Link href={tableItem.value.logsDownloadUrl} subtle={true}>{Resources.DownloadLogs}</Link>
                }
                </span>
            </SimpleTableCell>
        );
    }

    private readonly _columns: ITableColumn<TableListItem>[] = [
        {
            id: "id",
            name: Resources.Id,
            columnStyle: TableColumnStyle.Primary,
            width: new ObservableValue(-20),
            renderCell: this._renderIdCell
        } as ITableColumn<TableListItem>,
        {
            id: "errorsAndWarnings",
            name: Resources.ErrorsAndWarnings,
            columnStyle: TableColumnStyle.Secondary,
            width: new ObservableValue(-15),
            renderCell: this._renderErrorsCell
        } as ITableColumn<TableListItem>,
        {
            id: "queued",
            name: Resources.Queued,
            columnStyle: TableColumnStyle.Tertiary,
            width: new ObservableValue(-15),
            renderCell: this._renderQueuedTimeCell
        } as ITableColumn<TableListItem>,
        {
            id: "started",
            name: Resources.Started,
            columnStyle: TableColumnStyle.Tertiary,
            width: new ObservableValue(-15),
            renderCell: this._renderStartTimeCell
        } as ITableColumn<TableListItem>,
        {
            id: "duration",
            name: Resources.Duration,
            columnStyle: TableColumnStyle.Tertiary,
            width: new ObservableValue(-10),
            renderCell: this._renderDurationCell
        } as ITableColumn<TableListItem>,
        {
            id: "targetAgents",
            name: Resources.TargetAgents,
            columnStyle: TableColumnStyle.Tertiary,
            width: new ObservableValue(-15),
            renderCell: this._renderTargetAgentsCell
        } as ITableColumn<TableListItem>,
        {
            id: "logs",
            name: Resources.Logs,
            columnStyle: TableColumnStyle.Tertiary,
            width: new ObservableValue(-15),
            renderCell: this._renderLogsCell
        } as ITableColumn<TableListItem>
    ];
}
